import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useRouteMatch, Link } from 'react-router-dom';
import { get } from 'lodash';
import scrollIntoView from 'scroll-into-view';
import BoardDetail from 'components/base/BoardDetail';
import SearchInput from 'components/base/SearchInput';
import { getBoards, getBoard, createBoard, updateBoard, deleteBoard } from 'libs/api/board';
import { usePermission, usePagination, useDataApi } from 'libs/hooks';
import { TBoardDetail } from 'modules/board/types';
import { TListResponse, TPagination, RouteMatch } from 'modules/types';
import { CWindowWidth } from 'constants/base.const';
import { EOperation } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import { Avatar, Card, Col, Row, Form, List, Button, Typography } from 'antd';
import { EyeOutlined } from '@ant-design/icons/lib';
import styled from 'styled-components';
import BannerType03 from 'assets/images/banners/3.webp';

const { Text, Title } = Typography;
const maxWidth = CWindowWidth - 300;
export const CSLabBoardWrapper = styled.div`
  .title_wrapper {
    width: 100%;
    height: 300px;
    background-image: url(${BannerType03});
    background-size: cover;
    padding: 100px 10px;
    .ant-typography {
      width: 100%;
      max-width: ${maxWidth}px;
      color: #ffffff;
      margin: auto;
      display: block;
    }
  }
  .card .ant-avatar {
    object-fit: contain;
    height: 200px;
  }
  .card .ant-card-body {
    min-height: 120px;
  }
`;

const CSLabBoard = () => {
  const [isFirstRendering, setIsFirstRendering] = useState(true);
  const { adminPermission } = usePermission();
  const [pagination, setPagination] = usePagination(12);
  //페이지네이션을 통해 페이지라 리로드되면 pagenation state / isFirstRendering STATE 변경
  const reloadPage = (page?: Partial<TPagination>) => {
    setPagination({
      ...pagination,
      ...page,
    });
    setIsFirstRendering(false);
  };

  const getPromise = getBoards.bind(null, {
    params: {
      current: pagination.current,
      pageSize: pagination.pageSize,
      q: pagination.q,
    },
  });

  const [{ data, loading }, setCallback] = useDataApi<TListResponse<TBoardDetail>>(getPromise, {
    contents: [],
    last: false,
  });

  useEffect(() => {
    setCallback(() => getPromise);
  }, [pagination]);

  useEffect(() => {
    if (!loading && !isFirstRendering) {
      scrollIntoView(document?.getElementById('card-list-header') as HTMLElement, {
        align: {
          top: 0.3,
        },
      });
    }
  }, [loading]);

  return (
    <CSLabBoardWrapper>
      <Helmet>
        <title>CS 뉴스</title>
      </Helmet>
      <div className='title_wrapper'>
        <Title> CS 뉴스 </Title>
        <Text style={{ marginTop: '10px', fontSize: '16px' }}>
          {' '}
          멘티 분들을 위해 Career Step이 직접 기획·제작한 Content 입니다.{' '}
        </Text>
      </div>
      <div
        style={{
          maxWidth: `${maxWidth}px`,
          margin: '20px auto',
          padding: '0px',
        }}
      >
        <Row justify='center' align='middle' gutter={[8, 8]} id='card-list-header'>
          <Col>
            <SearchInput pagination={pagination} reloadPage={reloadPage} />
          </Col>
          {adminPermission && (
            <Col>
              <Link to={`${ERoute.CSLabBoard}/${EOperation.Create}`}>
                <Button className='add-button' type='primary' size='large'>
                  글쓰기
                </Button>
              </Link>
            </Col>
          )}
        </Row>
        <List
          pagination={{
            ...pagination,
            total: get(data, 'total', 0),
            position: 'bottom',
            onChange: (page) => {
              reloadPage({
                current: page,
                pageSize: pagination.pageSize,
              });
            },
          }}
          className='card'
          loading={loading}
          style={{ paddingBottom: '20px', marginTop: '30px' }}
          grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 3, xxl: 3 }}
          dataSource={get(data, 'contents')}
          renderItem={(item) => (
            <List.Item>
              <Link to={`${ERoute.CSLabBoard}/${EOperation.View}/${get(item, 'id')}`}>
                <Card hoverable cover={<Avatar shape='square' src={get(item, 'thumbnail.absolute_url')} />}>
                  <Card.Meta title={<div className='ellipsis-line'>{get(item, 'title')}</div>} />
                  <Row justify='start' style={{ position: 'absolute', bottom: '24px' }}>
                    <Col>
                      <EyeOutlined />
                      &nbsp;{get(item, 'hit_count')}
                    </Col>
                  </Row>
                </Card>
              </Link>
            </List.Item>
          )}
        />
      </div>
    </CSLabBoardWrapper>
  );
};

export const CSLabBoardDetail = () => {
  const match = useRouteMatch(`${ERoute.CSLabBoard}/:operation/:record_id?`);
  let { operation = EOperation.View, record_id } = (match?.params as RouteMatch) || {};

  const [{ data, loading }] = useDataApi<TBoardDetail>(
    getBoard.bind(null, record_id),
    {},
    operation != EOperation.Create,
  );

  return (
    <BoardDetail
      operation={operation}
      pathName={ERoute.CSLabBoard}
      promiseCreate={createBoard}
      promiseDelete={deleteBoard}
      promiseUpdate={updateBoard}
      activateComment={true}
      record={data}
      loading={loading}
    />
  );
};

export default CSLabBoard;
